// @flow
/**
 * Sets up the client side routing and causes intial 'React.render()' into the DOM.
 * Client side routing support mounted at 'myaccount/transfer/'.
 * Sets up React-Router support as well as defines the routes...
 */

// external libraries
import React from 'react'
import { hot } from 'react-hot-loader/root'
import { Route, Redirect, Switch } from 'react-router-dom'
import { ConnectedRouter as Router } from 'connected-react-router'
import { ThemeWrapper, PAYPAL_THEME } from '@paypalcorp/pp-react'
import { Provider } from 'react-redux' // eslint-disable-line no-restricted-imports
import DocumentTitle from 'react-document-title'
import { setShouldRTL } from '../lib/paypalphrodite'
import '../lib/datadog'

import MainContainer from './containers/main'
import Page from './containers/page'
import { is2WayCommentingTrmt } from '../experiments/chat'

import {
  splitOnEnter,
  redirectToRequestPreview,
  redirectToSplitPreview,
  redirectToHomepage,
} from './request-routes'

import './glamor-plugins'
import serverProps from './utils/serverProps'

import content from 'pp-react-l10n'

import generateReduxRoutes from './shared-core-routes'
import homepageRoutes from './homepage-routes'

import {
  RequestSuccess,
  MultiRequestSplitPreview,
  BillSplitRequest,
  BillSplittingPreview,
  BillSplittingSuccess,
  Reveal,
  Unilateral,
  UnilateralClaimMoney,
  GuestLogin,
  GuestHeader,
  GuestPayRequestRecipientHeader,
  GuestPreview,
  SendSuccessHeader,
  DisabledMassPayment,
  MoneyClaimedError,
  Chat,
  GuestInfoCollection,
  GuestRevampPostPaySignup,
  ULPClaimSharePage,
} from './lazy-components'
import GlobalToast from './components/global-toast/global-toast'
import { hasGuestFlowRevampForGuestOnlyCountries } from '../experiments/guest'
import * as fpti from '../lib/analytics/fpti'
const i18n = content('transfer/index')

// set the direction for styles (whether to convert them to RTL)
const { locality: { directionality = 'ltr' } = {} } = serverProps

setShouldRTL(directionality === 'rtl')

const { settingsError = '' } = serverProps

const reduxRoutes = generateReduxRoutes('/myaccount/transfer')
const homePage = homepageRoutes()

type Props = {
  store: {},
  history: {},
}
function App({ store, history }: Props) {
  return (
    <ThemeWrapper theme={PAYPAL_THEME}>
      <Provider store={store}>
        <DocumentTitle title={i18n('index.pageTitle')}>
          <Router history={history}>
            <Route path="/myaccount/transfer/">
              <MainContainer scrollToTop={true}>
                <Switch>
                  {/* Funnel Page is the default IF user is not part of the new home page experiment */}
                  <Route
                    exact
                    path="/myaccount/transfer"
                    render={() => (
                      <Redirect to="/myaccount/transfer/homepage" />
                    )}
                  />

                  {/* /myaccount/transfer/pay route handler */}
                  <Route path="/myaccount/transfer/pay">
                    <Page>
                      <Switch>
                        <Route
                          exact
                          path="/myaccount/transfer/pay"
                          render={() => (
                            <Redirect to="/myaccount/transfer/homepage/pay" />
                          )}
                        />
                      </Switch>
                    </Page>
                  </Route>

                  {/* Flow entry points */}
                  <Route path="/myaccount/transfer/request">
                    <Page>
                      <Switch>
                        <Route
                          exact
                          path="/myaccount/transfer/request"
                          render={props => (
                            <Redirect to="/myaccount/transfer/homepage/request" />
                          )}
                        />
                        <Route
                          path="/myaccount/transfer/request/external/:env?"
                          render={props => {
                            return (
                              splitOnEnter() ||
                              redirectToRequestPreview() || (
                                <BillSplitRequest
                                  {...props}
                                  flowType="request"
                                />
                              )
                            )
                          }}
                        />
                        <Redirect
                          from="/myaccount/transfer/request/external"
                          to="/myaccount/transfer/request"
                        />
                        <Route
                          path="/myaccount/transfer/request/preview"
                          render={props => {
                            return <MultiRequestSplitPreview {...props} />
                          }}
                        />
                        <Route
                          path="/myaccount/transfer/request/success"
                          render={props =>
                            redirectToHomepage(props) || (
                              <RequestSuccess {...props} />
                            )
                          }
                        />
                      </Switch>
                    </Page>
                  </Route>

                  <Route path="/myaccount/transfer/split">
                    <Page>
                      <Switch>
                        <Route
                          exact
                          path="/myaccount/transfer/split"
                          render={props =>
                            fpti.trackImpression(
                              'myaccount-transfer-split-removal',
                              'myaccount-transfer-split-removal'
                            ) ||
                            splitOnEnter(props) ||
                            redirectToRequestPreview() ||
                            redirectToSplitPreview() ||
                            null
                          }
                        />
                        <Route
                          path="/myaccount/transfer/split/preview"
                          render={props => {
                            return (
                              splitOnEnter(props) || (
                                <BillSplittingPreview
                                  billsplitPartner={true}
                                  {...props}
                                />
                              )
                            )
                          }}
                        />
                        <Route
                          path="/myaccount/transfer/split/success"
                          render={props =>
                            splitOnEnter(props) ||
                            redirectToHomepage(props) || (
                              <BillSplittingSuccess {...props} />
                            )
                          }
                        />
                        <Redirect
                          from="/myaccount/transfer/split/external"
                          to="/myaccount/transfer/request"
                        />
                      </Switch>
                    </Page>
                  </Route>

                  {is2WayCommentingTrmt() && (
                    <Route path="/myaccount/transfer/chat">
                      <Page>
                        <Switch>
                          <Route
                            exact
                            path="/myaccount/transfer/chat/transaction/:transactionId"
                            render={props => (
                              <Chat {...props} showConversationList={false} />
                            )}
                          />
                          <Route
                            exact
                            path="/myaccount/transfer/chat/request/:id/:legId?"
                            render={props => (
                              <Chat {...props} showConversationList={false} />
                            )}
                          />
                        </Switch>
                      </Page>
                    </Route>
                  )}

                  {homePage}

                  {reduxRoutes}

                  <Route
                    path="/myaccount/transfer/setting-up-mass-payments"
                    component={DisabledMassPayment}
                  />

                  <Route path="/myaccount/transfer/claim-money">
                    <Page>
                      <Switch>
                        <Route
                          exact
                          path="/myaccount/transfer/claim-money"
                          component={
                            settingsError
                              ? MoneyClaimedError
                              : UnilateralClaimMoney
                          }
                        />
                      </Switch>
                    </Page>
                  </Route>

                  <Route path="/myaccount/transfer/money-claimed">
                    <Page>
                      <Switch>
                        <Route
                          path="/myaccount/transfer/money-claimed"
                          render={props => <Unilateral {...props} />}
                        />
                      </Switch>
                    </Page>
                  </Route>

                  <Route path="/myaccount/transfer/money-claimed/:txnNum">
                    <Page>
                      <Switch>
                        <Route
                          path="/myaccount/transfer/money-claimed"
                          render={props => <Unilateral {...props} />}
                        />
                      </Switch>
                    </Page>
                  </Route>

                  {/* Guest */}
                  <Route
                    exact
                    path="/myaccount/transfer/guestLogin/payRequest/:id/:requestId"
                    render={props => {
                      return (
                        // GUEST 2.0 HERE
                        <GuestLogin
                          {...props}
                          body2={<GuestInfoCollection {...props} />}
                        />
                      )
                    }}
                  />

                  <Route
                    path="/myaccount/transfer/guestLogin/payRequest/:id/:requestId/preview"
                    render={props => {
                      return (
                        <GuestLogin
                          {...props}
                          header={<GuestHeader {...props} />}
                          body1={<GuestPayRequestRecipientHeader {...props} />}
                          body2={<GuestPreview {...props} />}
                        />
                      )
                    }}
                  />

                  <Route
                    path="/myaccount/transfer/guestLogin/payRequest/:id/:requestId/success"
                    render={props => {
                      return (
                        <GuestLogin
                          {...props}
                          header={<GuestHeader {...props} />}
                          body1={<SendSuccessHeader {...props} />}
                          body2={
                            !hasGuestFlowRevampForGuestOnlyCountries() && (
                              <GuestRevampPostPaySignup {...props} />
                            )
                          }
                        />
                      )
                    }}
                  />

                  <Route
                    exact
                    path="/myaccount/transfer/guestLogin/:env"
                    render={props => {
                      return (
                        <GuestLogin
                          {...props}
                          body1={<GuestPayRequestRecipientHeader {...props} />}
                          body2={<GuestInfoCollection {...props} />}
                        />
                      )
                    }}
                  />

                  <Route
                    exact
                    path="/myaccount/transfer/guestLogin/:env/preview"
                    render={props => {
                      return (
                        <GuestLogin
                          {...props}
                          header={<GuestHeader {...props} />}
                          body1={
                            <GuestPayRequestRecipientHeader
                              {...props}
                              isReview={true}
                            />
                          }
                          body2={<GuestPreview {...props} />}
                        />
                      )
                    }}
                  />

                  <Route
                    path="/myaccount/transfer/guestLogin/:env/success"
                    render={props => {
                      return (
                        <GuestLogin
                          {...props}
                          header={<GuestHeader {...props} />}
                          body1={<SendSuccessHeader {...props} />}
                          body2={
                            !hasGuestFlowRevampForGuestOnlyCountries() && (
                              <GuestRevampPostPaySignup {...props} />
                            )
                          }
                        />
                      )
                    }}
                  />

                  {/* Handles revealing gifts and themes to recipients */}
                  <Route
                    path="/myaccount/transfer/reveal"
                    component={settingsError ? MoneyClaimedError : Reveal}
                  />

                  {/* Handles revealing gifts and themes to recipients */}
                  <Route
                    path="/myaccount/transfer/reveal/:txnNum"
                    component={settingsError ? MoneyClaimedError : Reveal}
                  />

                  <Route
                    exact
                    path="/myaccount/transfer/claim-share"
                    component={props => <ULPClaimSharePage {...props} />}
                  />
                  {/* Catch all for invalid /transfer/* routes */}
                  {/* TODO: Test this. Doesn't seem to work on page refresh */}
                  {/* Consider turning this off in dev only? Makes dev much easier... */}
                  <Redirect from="*" to="/myaccount/transfer/" />
                </Switch>
                <GlobalToast />
              </MainContainer>
            </Route>
          </Router>
        </DocumentTitle>
      </Provider>
    </ThemeWrapper>
  )
}

export default hot(App)
